import type { AnyQuestion, QuestionAnswer } from '@kc/core/model/question'
import type { Rating } from '@kc/core/review/scheduling'
import type { Plan, QueueItem } from '@kc/core/review/plan'
import { itemScoring } from '@kc/core/review/plan'
import { currentOf, type SessionState } from '@kc/core/review/session'

export interface AnswerDraft {
  choice: number | null
  multi: number[]
  tf: boolean | null
  blanks: string[]
  order: number[]
  match: Record<number, number>
  matchSel: { side: 'left' | 'right'; index: number } | null
  text: string
  readingChoice: Record<number, number | null>
  readingTf: Record<number, boolean | null>
  readingBlanks: Record<number, string[]>
  readingText: Record<number, string>
}

export interface SessionResult {
  readonly title: string
  readonly type: string
  readonly rating: Rating | null
  readonly score: number | null
  readonly nextDueOn: number | null
  readonly requeue: boolean
}

export interface SessionViewData {
  readonly plan: Plan
  readonly state: SessionState
  readonly startedAt: number
  readonly results: readonly SessionResult[]
  readonly draft: AnswerDraft
}

const QUESTION_LABEL: Readonly<Record<string, string>> = {
  choice: '选择题',
  multiChoice: '多选题',
  trueFalse: '判断题',
  blank: '填空题',
  order: '排序题',
  match: '匹配题',
  freeRecall: '简答题',
  reading: '材料题',
}

const RATING_LABEL: Record<Rating, string> = {
  again: '重来',
  hard: '困难',
  good: '良好',
  easy: '简单',
}

const RATINGS: readonly Rating[] = ['again', 'hard', 'good', 'easy']

const esc = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char] as string)

const rich = (value: string): string =>
  esc(value).replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\n/g, '<br>')

/** 填空题按 ___ 切段后，代码 span 会被切断；这里剥掉落单的反引号，避免界面出现裸 `。 */
const blankRich = (value: string): string =>
  rich((value.match(/`/g)?.length ?? 0) % 2 === 1 ? value.replace(/`/g, '') : value)

const pad = (value: number): string => String(value).padStart(2, '0')
const formatClock = (seconds: number): string =>
  `${pad(Math.floor(seconds / 60))}:${pad(Math.floor(seconds % 60))}`

const todayEpoch = (): number => {
  const now = new Date()
  return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86_400_000)
}

const formatDue = (day: number | null): string => {
  if (day === null) return '—'
  const today = todayEpoch()
  if (day === today) return '今天'
  if (day === today + 1) return '明天'
  return new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric' }).format(
    new Date(day * 86_400_000),
  )
}

const hashString = (value: string): number => {
  let hash = 2166136261
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function shuffledIndices(count: number, seed: string): number[] {
  const values = Array.from({ length: count }, (_, i) => i)
  let state = hashString(seed)
  for (let i = count - 1; i > 0; i--) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    const j = state % (i + 1)
    ;[values[i], values[j]] = [values[j] as number, values[i] as number]
  }
  return values
}

export function emptyDraft(): AnswerDraft {
  return {
    choice: null,
    multi: [],
    tf: null,
    blanks: [],
    order: [],
    match: {},
    matchSel: null,
    text: '',
    readingChoice: {},
    readingTf: {},
    readingBlanks: {},
    readingText: {},
  }
}

export function draftFor(item: QueueItem | null): AnswerDraft {
  const draft = emptyDraft()
  if (item?.subject.kind !== 'question') return draft
  const q = item.subject.question
  if (q.kind === 'blank') {
    draft.blanks = Array(q.data.blanks.length).fill('')
  } else if (q.kind === 'reading') {
    q.data.questions.forEach((sub, index) => {
      if (sub.kind === 'blank') {
        const blanks = (sub as unknown as { data: { blanks: readonly unknown[] } }).data.blanks
        draft.readingBlanks[index] = Array(blanks.length).fill('')
      }
    })
  }
  return draft
}

function itemTitle(item: QueueItem): string {
  return item.subject.concept.title
}

function itemType(item: QueueItem): string {
  return item.subject.kind === 'concept'
    ? '知识点卡'
    : QUESTION_LABEL[item.subject.question.kind] ?? item.subject.question.kind
}

function itemStateText(item: QueueItem): string {
  if (item.reason === 'new') return '新卡 · 第一次见'
  const reps = item.state?.reps ?? 0
  if (item.reason === 'due' && item.overdue > 0) return `到期 ${item.overdue} 天 · 第 ${reps + 1} 次复习`
  return `第 ${reps + 1} 次复习`
}

export function canSubmit(item: QueueItem, draft: AnswerDraft): boolean {
  if (item.subject.kind === 'concept') return false
  const q = item.subject.question
  switch (q.kind) {
    case 'choice':
    case 'trueFalse':
      return q.kind === 'choice' ? draft.choice !== null : draft.tf !== null
    case 'multiChoice':
      return draft.multi.length > 0
    case 'blank':
      return q.data.blanks.every((_, index) => (draft.blanks[index] ?? '').trim().length > 0)
    case 'order':
      return draft.order.length === q.data.items.length
    case 'match':
      return Object.keys(draft.match).length === q.data.pairs.length
    case 'freeRecall':
      return draft.text.trim().length > 0
    case 'reading':
      return q.data.questions.every((sub, index) => {
        if (sub.kind === 'choice') return draft.readingChoice[index] !== undefined && draft.readingChoice[index] !== null
        if (sub.kind === 'trueFalse') return draft.readingTf[index] !== undefined && draft.readingTf[index] !== null
        if (sub.kind === 'blank') {
          return (draft.readingBlanks[index] ?? []).every((value) => value.trim().length > 0)
        }
        if (sub.kind === 'freeRecall') return (draft.readingText[index] ?? '').trim().length > 0
        return false
      })
  }
}

export function buildAnswer(item: QueueItem, draft: AnswerDraft): QuestionAnswer {
  if (item.subject.kind === 'concept') throw new Error('知识点卡没有 QuestionAnswer')
  const q = item.subject.question
  switch (q.kind) {
    case 'choice':
      return { kind: 'choice', value: draft.choice ?? 0 }
    case 'multiChoice':
      return { kind: 'multiChoice', value: [...draft.multi].sort((a, b) => a - b) }
    case 'trueFalse':
      return { kind: 'trueFalse', value: draft.tf ?? false }
    case 'blank':
      return { kind: 'blank', value: draft.blanks }
    case 'order':
      return { kind: 'order', value: draft.order }
    case 'match': {
      const value: Record<string, string> = {}
      q.data.pairs.forEach(([left, right], leftIndex) => {
        const rightIndex = draft.match[leftIndex]
        if (rightIndex !== undefined) value[left] = q.data.pairs[rightIndex]?.[1] ?? right
      })
      return { kind: 'match', value }
    }
    case 'freeRecall':
      return { kind: 'freeRecall', value: draft.text }
    case 'reading': {
      const values = q.data.questions.map((sub, index) => {
        if (sub.kind === 'choice') return { kind: 'choice', value: draft.readingChoice[index] ?? 0 } as const
        if (sub.kind === 'trueFalse') return { kind: 'trueFalse', value: draft.readingTf[index] ?? false } as const
        if (sub.kind === 'blank') return { kind: 'blank', value: draft.readingBlanks[index] ?? [] } as const
        if (sub.kind === 'freeRecall') return { kind: 'freeRecall', value: draft.readingText[index] ?? '' } as const
        throw new Error(`材料题子题类型 ${sub.kind} 暂未接输入`)
      })
      return { kind: 'reading', value: values }
    }
  }
}

function meta(item: QueueItem): string {
  return `<div class="session-meta">
    <span class="session-concept">${esc(item.subject.concept.title)}</span>
    <span class="session-dot"></span>
    <span class="session-kind">${esc(itemType(item))}</span>
    <span class="session-dot"></span>
    <span>${esc(itemStateText(item))}</span>
  </div>`
}

function prompt(item: QueueItem): string {
  if (item.subject.kind === 'concept') {
    return `<div class="session-prompt">${esc(item.subject.concept.title)}</div>
      <p class="session-lede">合上笔记，按段落标题复述这个知识点。想到什么写什么，想好后再显示要点。</p>`
  }
  return `<div class="session-prompt">${rich(item.subject.question.prompt)}</div>`
}

function choiceOptions(
  q: AnyQuestion,
  draft: AnswerDraft,
  locked: boolean,
  control: 'choice' | 'reading-choice' = 'choice',
): string {
  if (q.kind !== 'choice' && q.kind !== 'multiChoice') return ''
  const multi = q.kind === 'multiChoice'
  const correct = multi ? q.data.answers : [q.data.answer]
  const selected = multi ? draft.multi : draft.choice === null ? [] : [draft.choice]
  return `<div class="session-options">
    ${q.data.options
      .map((option, index) => {
        const isCorrect = locked && correct.includes(index)
        const isWrong = locked && selected.includes(index) && !correct.includes(index)
        const isPicked = !locked && selected.includes(index)
        return `<button class="session-option ${isPicked ? 'is-selected' : ''} ${isCorrect ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}"
          data-session="${multi ? 'multi' : control}" data-value="${index}" ${locked ? 'disabled' : ''}>
          <span class="session-key">${String.fromCharCode(65 + index)}</span>
          <span>${rich(option)}</span>
          <span class="session-mark">${isCorrect ? '正确答案' : isWrong ? '你的选择' : ''}</span>
        </button>`
      })
      .join('')}
  </div>`
}

function readingSubView(
  sub: AnyQuestion,
  draft: AnswerDraft,
  index: number,
  locked: boolean,
): string {
  if (sub.kind === 'choice') {
    const selected = draft.readingChoice[index] ?? null
    return `<div class="session-options">${sub.data.options
      .map((option, optionIndex) => {
        const isCorrect = locked && sub.data.answer === optionIndex
        const isWrong = locked && selected === optionIndex && !isCorrect
        return `<button class="session-option ${selected === optionIndex ? 'is-selected' : ''} ${isCorrect ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}"
          data-session="reading-choice" data-index="${index}" data-value="${optionIndex}" ${locked ? 'disabled' : ''}>
          <span class="session-key">${String.fromCharCode(65 + optionIndex)}</span>
          <span>${rich(option)}</span>
          <span class="session-mark">${isCorrect ? '正确答案' : isWrong ? '你的选择' : ''}</span>
        </button>`
      })
      .join('')}</div>`
  }
  if (sub.kind === 'trueFalse') {
    const selected = draft.readingTf[index] ?? null
    return `<div class="tf-grid">${[
      [true, '对'],
      [false, '错'],
    ]
      .map(([value, label]) => {
        const correct = locked && sub.data.expects === value
        const wrong = locked && selected === value && !correct
        return `<button class="tf-btn ${selected === value ? 'is-selected' : ''} ${correct ? 'is-correct' : ''} ${wrong ? 'is-wrong' : ''}"
          data-session="reading-tf" data-index="${index}" data-value="${value}" ${locked ? 'disabled' : ''}>${label}</button>`
      })
      .join('')}</div>`
  }
  if (sub.kind === 'blank') {
    return `<div class="blank-text">${sub.data.text
      .split('___')
      .map((part, partIndex, parts) => {
        const value = draft.readingBlanks[index]?.[partIndex] ?? ''
        const input = `<input class="blank-input" data-session-input="reading-blank" data-index="${index}" data-blank-index="${partIndex}"
          id="reading-blank-${index}-${partIndex}" name="reading-blank-${index}-${partIndex}"
          type="text" value="${esc(value)}" ${locked ? 'disabled' : ''}
          inputmode="text" enterkeyhint="next" autocomplete="one-time-code" autocapitalize="off" autocorrect="off" spellcheck="false"
          data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
          aria-label="第 ${partIndex + 1} 个空" placeholder="填空">`
        return `${blankRich(part)}${partIndex < parts.length - 1 ? input : ''}`
      })
      .join('')}</div>`
  }
  if (sub.kind === 'freeRecall') {
    return `<textarea class="recall-area" data-session-input="reading-text" data-index="${index}" ${locked ? 'disabled' : ''}
      placeholder="写下你的答案…">${esc(draft.readingText[index] ?? '')}</textarea>`
  }
  return `<p class="session-lede">这个材料子题类型（${esc(sub.kind)}）暂未接输入。</p>`
}

function answerView(item: QueueItem, draft: AnswerDraft, phase: SessionState['phase']): string {
  const locked = phase !== 'answering'
  if (item.subject.kind === 'concept') {
    if (!locked) return ''
    return `<div class="recall-points">
      ${item.subject.recall.headings
        .map((heading, index) => `<div class="point-row"><span>${pad(index + 1)}</span>${esc(heading)}</div>`)
        .join('')}
    </div>`
  }

  const q = item.subject.question
  switch (q.kind) {
    case 'choice':
    case 'multiChoice':
      return choiceOptions(q, draft, locked)
    case 'trueFalse':
      return `<div class="tf-grid">
        ${[
          [true, '对'],
          [false, '错'],
        ]
          .map(([value, label]) => {
            const picked = draft.tf === value
            const correct = locked && q.data.expects === value
            const wrong = locked && picked && q.data.expects !== value
            return `<button class="tf-btn ${picked ? 'is-selected' : ''} ${correct ? 'is-correct' : ''} ${wrong ? 'is-wrong' : ''}"
              data-session="tf" data-value="${value}" ${locked ? 'disabled' : ''}>${label}</button>`
          })
          .join('')}
      </div>`
    case 'blank':
      return `<div class="blank-text">${q.data.text
        .split('___')
        .map((part, index, parts) => {
        const input = `<input class="blank-input" data-session-input="blank" data-index="${index}"
          id="blank-${index}" name="blank-${index}"
          type="text" value="${esc(draft.blanks[index] ?? '')}" ${locked ? 'disabled' : ''}
          inputmode="text" enterkeyhint="next" autocomplete="one-time-code" autocapitalize="off" autocorrect="off" spellcheck="false"
          data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
          aria-label="第 ${index + 1} 个空" placeholder="填一个词">`
          return `${blankRich(part)}${index < parts.length - 1 ? input : ''}`
        })
        .join('')}</div>
        <div class="blank-hint"><span>${q.data.blanks.length} 个空位</span><span>提交后逐空判定</span></div>`
    case 'order': {
      const display = shuffledIndices(q.data.items.length, q.id)
      return `<div class="order-list">
        ${display
          .map((index) => {
            const position = draft.order.indexOf(index)
            return `<button class="order-item ${position >= 0 ? 'is-picked' : ''}" data-session="order" data-value="${index}" ${locked ? 'disabled' : ''}>
              <span class="ord-badge">${position >= 0 ? position + 1 : ''}</span>
              <span>${rich(q.data.items[index] ?? '')}</span>
            </button>`
          })
          .join('')}
      </div>
      <div class="order-hint"><span>按正确顺序依次点击，再点一次取消</span><span>${draft.order.length} / ${q.data.items.length}</span></div>`
    }
    case 'match': {
      const rightOrder = shuffledIndices(q.data.pairs.length, `${q.id}:right`)
      const pairOf = (leftIndex: number): number | null => draft.match[leftIndex] ?? null
      const rows = q.data.pairs
        .map(([left], leftIndex) => {
          const rightIndex = rightOrder[leftIndex] ?? leftIndex
          const right = pairOf(leftIndex)
          const leftSelected = draft.matchSel?.side === 'left' && draft.matchSel.index === leftIndex
          const pairedLeft = Object.keys(draft.match).find(
            (key) => draft.match[Number(key)] === rightIndex,
          )
          const rightSelected = draft.matchSel?.side === 'right' && draft.matchSel.index === rightIndex
          const leftButton = `<button class="match-item ${right !== null ? 'is-paired' : ''} ${leftSelected ? 'is-sel' : ''}"
            data-session="match-left" data-value="${leftIndex}" ${locked ? 'disabled' : ''}>
            <span class="match-num">${right === null ? '' : right + 1}</span>${esc(left)}
          </button>`
          const rightButton = `<button class="match-item ${pairedLeft !== undefined ? 'is-paired' : ''} ${rightSelected ? 'is-sel' : ''}"
            data-session="match-right" data-value="${rightIndex}" ${locked ? 'disabled' : ''}>
            <span class="match-num">${pairedLeft === undefined ? '' : Number(pairedLeft) + 1}</span>${esc(q.data.pairs[rightIndex]?.[1] ?? '')}
          </button>`
          return leftButton + rightButton
        })
        .join('')
      return `<div class="match-grid">
        <div class="match-head">左侧</div><div class="match-head">右侧</div>
        ${rows}
      </div>
      <div class="match-note">先点左侧，再点右侧；点已配对的一项可以取消。</div>`
    }
    case 'freeRecall':
      return `<textarea class="recall-area" data-session-input="recall" ${locked ? 'disabled' : ''}
        placeholder="凭记忆写下答案，不必完整…">${esc(draft.text)}</textarea>
        <div class="recall-tip">简答题不自动判分：先看参考答案，再自评档位。</div>`
    case 'reading': {
      return `<div class="passage"><div class="passage-head"><span>READING</span><span>MATERIAL</span></div><pre>${esc(q.data.passage)}</pre></div>
        ${q.data.questions
          .map(
            (sub, index) => `<div class="sub-q"><div class="sub-q-head"><span>${pad(index + 1)}</span>${rich(sub.prompt)}</div>${readingSubView(sub as AnyQuestion, draft, index, locked)}</div>`,
          )
          .join('')}`
    }
    default:
      return ''
  }
}

function reference(item: QueueItem): string {
  if (item.subject.kind === 'concept') return ''
  const q = item.subject.question
  if (q.kind === 'freeRecall') {
    return `<div class="reference"><div class="ref-label">参考答案</div><p>${esc(q.data.reference)}</p>
      <div class="keyword-row"><em>关键词</em>${(q.data.keywords ?? []).map((word) => `<span>${esc(word)}</span>`).join('')}</div></div>`
  }
  return ''
}

function ratingButtons(): string {
  return `<div class="rating-block">
    <div class="rating-label"><span>这次记得怎么样？</span><span>自评决定下次间隔</span></div>
    <div class="ratings">${RATINGS.map(
      (rating) => `<button class="rating-btn" data-session="rate" data-value="${rating}">
        <strong>${RATING_LABEL[rating]}</strong>
      </button>`,
    ).join('')}</div>
  </div>`
}

function feedback(item: QueueItem, state: SessionState): string {
  if (state.phase === 'reporting') {
    return `<div class="session-feedback"><div class="feedback-line"><span class="feedback-badge self">自评</span><span>对照要点，给自己选一个档位。</span></div>${reference(item)}${ratingButtons()}</div>`
  }
  if (state.phase !== 'committed') return ''

  const receipt = state.receipt
  if (receipt.kind === 'rejected') {
    return `<div class="session-feedback"><div class="feedback-line"><span class="feedback-badge bad">未记录</span><span>这张卡暂时无法提交。</span></div>
      <pre class="feedback-error">${receipt.issues.map((issue) => `${issue.code}: ${issue.message}`).join('\n')}</pre></div>`
  }
  if (receipt.kind === 'readOnly') {
    return `<div class="session-feedback"><div class="feedback-line"><span class="feedback-badge self">只读</span><span>预览轮不写日志。</span></div></div>`
  }

  const result = receipt.result
  const outcome = result.outcome
  const score = outcome?.score ?? null
  const badge =
    score === null
      ? { cls: 'self', text: RATING_LABEL[result.rating] }
      : score === 1
        ? { cls: 'good', text: '正确' }
        : score >= 0.5
          ? { cls: 'partial', text: `部分正确 · ${score.toFixed(2)}` }
          : { cls: 'bad', text: '未答对' }
  return `<div class="session-feedback">
    <div class="feedback-line">
      <span class="feedback-badge ${badge.cls}">${badge.text}</span>
      <span>评分 <strong>${RATING_LABEL[result.rating]}</strong> · 下次 ${formatDue(result.nextDueOn)}</span>
    </div>
    ${outcome === undefined || outcome === null ? reference(item) : `<div class="answer-block"><div class="ref-label">正确答案</div><p>${esc(outcome.correctAnswer)}</p></div>`}
    ${outcome !== null && item.subject.kind === 'question' && item.subject.question.explain !== null ? `<div class="explain">${rich(item.subject.question.explain)}</div>` : ''}
  </div>`
}

function footer(item: QueueItem, state: SessionState, draft: AnswerDraft): string {
  const self = itemScoring(item) === 'self'
  let label = '提交答案'
  let action = 'session-submit'
  let disabled = !canSubmit(item, draft)

  if (state.phase === 'answering') {
    if (self) {
      label = '显示参考答案'
      action = 'session-reveal'
      disabled = false
    }
  } else if (state.phase === 'reporting') {
    label = '先选一个档位'
    action = 'session-wait'
    disabled = true
  } else if (state.phase === 'committed') {
    label = state.cursor + 1 >= state.session.items.length ? '完成本轮' : '下一题'
    action = 'session-next'
    disabled = false
  }

  return `<div class="session-foot"><div class="session-foot-inner">
    <div class="session-hint">${state.phase === 'answering' ? (self ? '想好后显示参考答案' : '作答后提交') : state.phase === 'reporting' ? '自评后记录' : '提交后已写入本地日志'}</div>
    <button class="btn btn-primary" id="session-action" data-session-action="${action}" ${disabled ? 'disabled' : ''}>${label}</button>
  </div></div>`
}

export function sessionView(data: SessionViewData): string {
  const state = data.state
  if (state.phase === 'idle' || state.phase === 'ended') return ''
  const item = currentOf(state)
  if (item === null) return ''

  const total = state.session.items.length
  const segments = Array.from({ length: total }, (_, index) => {
    const cls = index < state.cursor ? 'done' : index === state.cursor ? 'current' : ''
    return `<i class="seg ${cls}"></i>`
  }).join('')

  return `<div class="session-screen">
    <header class="session-top">
      <button class="session-quit" data-action="exit-session">← 退出</button>
      <div class="session-progress">
        <div class="session-count"><span>${pad(state.cursor + 1)}</span><span class="slash">/</span><span>${pad(total)}</span></div>
        <div class="segbar">${segments}</div>
      </div>
      <span class="session-time" id="session-time">${formatClock((Date.now() - data.startedAt) / 1000)}</span>
    </header>
    <div class="session-scroll"><div class="session-inner">
      ${meta(item)}
      ${prompt(item)}
      ${answerView(item, data.draft, state.phase)}
      ${feedback(item, state)}
    </div></div>
    ${footer(item, state, data.draft)}
  </div>`
}

export function summaryView(data: SessionViewData, elapsed: number): string {
  const scored = data.results.filter((result) => result.score !== null)
  const avg = scored.length === 0 ? 0 : scored.reduce((sum, result) => sum + (result.score ?? 0), 0) / scored.length
  const counts: Record<Rating, number> = { again: 0, hard: 0, good: 0, easy: 0 }
  data.results.forEach((result) => {
    if (result.rating !== null) counts[result.rating] += 1
  })

  return `<div class="summary-screen"><div class="summary-inner">
    <p class="eyebrow">本轮复盘</p>
    <h1 class="summary-title">这一轮结束了。</h1>
    <p class="summary-lede">${data.results.length} 张卡已提交到本地日志；下次到期日已经按间隔推进。</p>
    <div class="summary-metrics">
      <div><span>完成</span><strong>${data.results.length}</strong><small>张</small></div>
      <div><span>正确率</span><strong>${Math.round(avg * 100)}</strong><small>%</small></div>
      <div><span>用时</span><strong>${formatClock(elapsed)}</strong></div>
    </div>
    <div class="summary-ratings">${RATINGS.map(
      (rating) => `<div><span>${RATING_LABEL[rating]}</span><strong>${counts[rating]}</strong></div>`,
    ).join('')}</div>
    <div class="summary-list">${data.results
      .map((result) => `<div class="summary-row">
        <i class="summary-dot ${result.rating ?? 'good'}"></i>
        <span>${esc(result.title)}</span>
        <em>${esc(result.type)}</em>
        <small>${formatDue(result.nextDueOn)}</small>
      </div>`)
      .join('')}</div>
    <div class="summary-actions">
      <button class="btn btn-primary" data-action="summary-back">回到今日</button>
      <button class="btn btn-ghost" data-action="summary-restart">再复习一轮</button>
    </div>
    <p class="summary-foot">${esc(data.plan.planId)} · ${data.results.length} 张</p>
  </div></div>`
}
